import { Fragment, useRef, useState } from 'react';
import { useFinancieelStore } from '../../stores';
import type { KostenRegel } from '../../stores/financieel-store';

function formatTransactieDatum(datum: string): string {
  return new Date(datum + 'T00:00:00').toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

function formatAmount(amount: number): string {
  return new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

function formatMonthLabel(month: string): string {
  const parts = month.split('-').map(Number);
  const d = new Date(parts[0]!, parts[1]! - 1, 1);
  const label = d.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function exportCSV(data: KostenRegel[], month: string) {
  const BOM = '\uFEFF';
  const header = 'Rekening;Omschrijving;Debet;Credit;Netto;Boekingen';
  const formatNL = (n: number) => n.toFixed(2).replace('.', ',');

  const rows = data.map(r =>
    `${r.acc_id};${r.acc_desc};${formatNL(r.amount_debit)};${formatNL(r.amount_credit)};${formatNL(r.net_amount)};${r.transaction_count}`
  );

  const totalDebit = data.reduce((s, r) => s + r.amount_debit, 0);
  const totalCredit = data.reduce((s, r) => s + r.amount_credit, 0);
  const totalNet = data.reduce((s, r) => s + r.net_amount, 0);
  const totalCount = data.reduce((s, r) => s + r.transaction_count, 0);
  rows.push(`;;${formatNL(totalDebit)};${formatNL(totalCredit)};${formatNL(totalNet)};${totalCount}`);

  const content = BOM + header + '\n' + rows.join('\n');
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `kosten-${month}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function KostenOverzicht() {
  const {
    selectedMonth, kostenData, yukiBestand, isUploadingYuki, uploadYukiBestand,
    kostenTransacties, loadKostenTransacties,
  } = useFinancieelStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [expandedAcc, setExpandedAcc] = useState<string | null>(null);

  const totalNet = kostenData.reduce((s, r) => s + r.net_amount, 0);

  const toggleRow = (accId: string) => {
    if (expandedAcc === accId) {
      setExpandedAcc(null);
    } else {
      setExpandedAcc(accId);
      void loadKostenTransacties(accId);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadYukiBestand(file);
    // Reset input so same file can be re-uploaded
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="bg-white rounded-card shadow-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-main">Kosten {formatMonthLabel(selectedMonth)}</h2>
        <div className="flex items-center gap-2">
          <a
            href="https://cincocleaning.yukiworks.nl/domain/financial/aspx/exporttransactions.aspx?__subframe=1"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Yuki
          </a>
          <button
            onClick={() => exportCSV(kostenData, selectedMonth)}
            disabled={kostenData.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploadingYuki}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 disabled:opacity-50 transition-colors"
          >
            {isUploadingYuki ? (
              <>
                <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                Uploaden...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Upload XAF
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xaf,.xml"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      </div>

      {kostenData.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-gray-400 mb-3">Nog geen Yuki export geupload voor deze maand</p>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploadingYuki}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 disabled:opacity-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Upload Yuki XAF bestand
          </button>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/80">
                  <th className="py-2 px-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Rekening</th>
                  <th className="py-2 px-3 text-left text-xs font-medium text-muted uppercase tracking-wider">Omschrijving</th>
                  <th className="py-2 px-3 text-right text-xs font-medium text-muted uppercase tracking-wider">Bedrag</th>
                  <th className="py-2 px-3 text-right text-xs font-medium text-muted uppercase tracking-wider">Boekingen</th>
                  <th className="py-2 px-3 text-right text-xs font-medium text-muted uppercase tracking-wider">%</th>
                </tr>
              </thead>
              <tbody>
                {kostenData.map(row => {
                  const isExpanded = expandedAcc === row.acc_id;
                  const transacties = kostenTransacties[`${selectedMonth}|${row.acc_id}`];
                  return (
                    <Fragment key={row.acc_id}>
                      <tr
                        className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer"
                        onClick={() => toggleRow(row.acc_id)}
                      >
                        <td className="py-2 px-3 text-sm text-gray-500">
                          <span className="inline-flex items-center gap-1.5">
                            <svg
                              className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none" viewBox="0 0 24 24" stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                            {row.acc_id}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-sm text-main">{row.acc_desc}</td>
                        <td className="py-2 px-3 text-sm text-right tabular-nums">{formatAmount(row.net_amount)}</td>
                        <td className="py-2 px-3 text-sm text-right tabular-nums">{row.transaction_count}</td>
                        <td className="py-2 px-3 text-sm text-right tabular-nums text-gray-500">
                          {totalNet > 0 ? Math.round(row.net_amount / totalNet * 100) + '%' : '-'}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="border-b border-gray-100">
                          <td colSpan={5} className="py-0 px-3 bg-gray-50/60">
                            {!transacties ? (
                              <p className="py-3 pl-8 text-xs text-gray-400">Laden…</p>
                            ) : transacties.length === 0 ? (
                              <p className="py-3 pl-8 text-xs text-gray-400">
                                Geen transactiedetails beschikbaar — wordt gevuld bij de volgende nachtelijke sync
                              </p>
                            ) : (
                              <div className="py-2 pl-8 pr-2 max-h-64 overflow-y-auto">
                                {transacties.map((t, i) => (
                                  <div
                                    key={i}
                                    className="flex items-baseline justify-between gap-4 py-1 text-xs border-b border-gray-100 last:border-0"
                                  >
                                    <span className="text-gray-400 w-14 shrink-0 tabular-nums">
                                      {formatTransactieDatum(t.datum)}
                                    </span>
                                    <span className="text-gray-600 flex-1 truncate" title={t.omschrijving}>
                                      {t.omschrijving || '—'}
                                    </span>
                                    <span className={`tabular-nums shrink-0 ${t.bedrag < 0 ? 'text-emerald-600' : 'text-gray-700'}`}>
                                      {formatAmount(t.bedrag)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-50/80 font-semibold">
                  <td className="py-2 px-3 text-xs uppercase text-muted" />
                  <td className="py-2 px-3 text-sm">Totaal</td>
                  <td className="py-2 px-3 text-sm text-right tabular-nums">{formatAmount(totalNet)}</td>
                  <td className="py-2 px-3 text-sm text-right tabular-nums">
                    {kostenData.reduce((s, r) => s + r.transaction_count, 0)}
                  </td>
                  <td className="py-2 px-3 text-sm text-right tabular-nums">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {yukiBestand && (
            <p className="mt-3 text-xs text-gray-400">
              Bron: {yukiBestand.bestand_naam}, geupload{' '}
              {new Date(yukiBestand.uploaded_at).toLocaleDateString('nl-NL', {
                day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            </p>
          )}
        </>
      )}
    </div>
  );
}
